import React, { useState } from "react";
import { Modal } from "semantic-ui-react";

// Import wizard step components
import AuditStep from "./AuditStep";
import GroupStep from "./GroupStep";
import ReviewStep from "./ReviewStep";
import InventoryStep from "./InventoryStep";
import CompletedStep from "./CompletedStep";
import WizardStepsHeader, { VIEW_MODES } from "./WizardStepsHeader";

const SplitInventoryWizard = ({
  open,
  onClose,
  inventory,
  supplyUnits,
  setSupplyUnits,
  onComplete,
  isLoading,
}) => {
  // Define wizard modes (internal to wizard)
  const WIZARD_MODES = {
    AUDIT: "audit",
    GROUP: "group",
    INVENTORY: "inventory",
    REVIEW: "review",
    COMPLETED: "completed",
  };

  const [wizardMode, setWizardMode] = useState(WIZARD_MODES.AUDIT);

  // Handle wizard completion
  const handleFinish = () => {
    onComplete(); // Notify parent that wizard is complete
    onClose(); // Close the wizard modal
    setWizardMode(WIZARD_MODES.AUDIT); // Reset to first step for next time
  };

  // Map wizard modes to VIEW_MODES for the wizard component
  const getViewModeForWizard = () => {
    switch (wizardMode) {
      case WIZARD_MODES.AUDIT:
        return VIEW_MODES.AUDIT;
      case WIZARD_MODES.GROUP:
        return VIEW_MODES.GROUP;
      case WIZARD_MODES.INVENTORY:
        return VIEW_MODES.INVENTORY;
      case WIZARD_MODES.REVIEW:
        return VIEW_MODES.REVIEW;
      case WIZARD_MODES.COMPLETED:
        return VIEW_MODES.COMPLETED;
      default:
        return VIEW_MODES.AUDIT;
    }
  };

  // Get header text based on current wizard mode
  const getHeaderText = () => {
    switch (wizardMode) {
      case WIZARD_MODES.AUDIT:
        return "Audit Inventory Items";
      case WIZARD_MODES.GROUP:
        return "Group Inventory Items";
      case WIZARD_MODES.INVENTORY:
        return "Add Inventory Details";
      case WIZARD_MODES.REVIEW:
        return "Review Inventory Changes";
      case WIZARD_MODES.COMPLETED:
        return "Inventory Changes Complete";
      default:
        return "Audit Inventory Items";
    }
  };

  // Get description text based on current wizard mode
  const getHeaderDescription = () => {
    switch (wizardMode) {
      case WIZARD_MODES.AUDIT:
        return "Audit inventory and select items to reassign";
      case WIZARD_MODES.GROUP:
        return "Group items that should be assigned to the same inventory ID";
      case WIZARD_MODES.INVENTORY:
        return "Add shelf and title to new inventory records";
      case WIZARD_MODES.REVIEW:
        return "Review your changes before submission";
      case WIZARD_MODES.COMPLETED:
        return "Your inventory changes have been processed successfully";
      default:
        return "";
    }
  };

  // Handle closing wizard (reset state)
  const handleClose = () => {
    setWizardMode(WIZARD_MODES.AUDIT); // Reset to first step
    onClose();
  };

  // Render content based on wizard mode
  const renderWizardContent = () => {
    switch (wizardMode) {
      case WIZARD_MODES.AUDIT:
        return (
          <AuditStep
            setSupplyUnits={setSupplyUnits}
            supplyUnits={supplyUnits}
            inventory={inventory}
            onStepComplete={() => {
              setWizardMode(WIZARD_MODES.GROUP);
            }}
            onPrevious={handleClose}
          />
        );

      case WIZARD_MODES.GROUP:
        return (
          <GroupStep
            supplyUnits={supplyUnits}
            setSupplyUnits={setSupplyUnits}
            onStepComplete={(formValues) => {
              setWizardMode(WIZARD_MODES.INVENTORY);
            }}
            onPrevious={() => setWizardMode(WIZARD_MODES.AUDIT)}
          />
        );

      case WIZARD_MODES.INVENTORY:
        return (
          <InventoryStep
            inventory={inventory}
            supplyUnits={supplyUnits}
            setSupplyUnits={setSupplyUnits}
            onStepComplete={(formValues) => {
              setWizardMode(WIZARD_MODES.REVIEW);
            }}
            onPrevious={() => setWizardMode(WIZARD_MODES.GROUP)}
          />
        );

      case WIZARD_MODES.REVIEW:
        return (
          <ReviewStep
            inventory={inventory}
            supplyUnits={supplyUnits}
            setSupplyUnits={setSupplyUnits}
            onStepComplete={() => {
              setWizardMode(WIZARD_MODES.COMPLETED);
            }}
            onPrevious={() => setWizardMode(WIZARD_MODES.INVENTORY)}
          />
        );

      case WIZARD_MODES.COMPLETED:
        return (
          <CompletedStep
            supplyUnits={supplyUnits}
            onFinish={handleFinish}
            setSupplyUnits={setSupplyUnits}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={handleClose} closeOnDimmerClick size="large">
      <Modal.Header>
        {getHeaderText()}
        <div
          style={{
            fontSize: "0.7em",
            marginTop: "5px",
            fontWeight: "normal",
          }}
        >
          {getHeaderDescription()}
        </div>
      </Modal.Header>
      <Modal.Content>
        <WizardStepsHeader
          viewMode={getViewModeForWizard()}
          isLoading={isLoading}
        />
        {renderWizardContent()}
      </Modal.Content>
      {/* Modal actions are handled by individual steps */}
    </Modal>
  );
};

export default SplitInventoryWizard;
